import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ToneDot } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';

/**
 * Header status: "● Gemini · live data · 2 safe actions" (green), or "● AI not configured" (red).
 * status: { configured, provider_label, model, safe_actions: [{ key, label }] } | null while loading.
 */
export default function AiStatusPill({ status, error }) {
  const loading = !status && !error;
  const configured = Boolean(status?.configured);
  const actions = status?.safe_actions || [];
  const tone = loading || error ? 'gray' : configured ? 'green' : 'red';

  let main;
  let muted;
  if (loading) {
    main = 'Checking AI…';
  } else if (error) {
    main = 'AI status unknown';
  } else if (configured) {
    main = status.provider_label || 'Gemini';
    muted = ` · live data · ${actions.length} safe ${actions.length === 1 ? 'action' : 'actions'}`;
  } else {
    main = 'AI not configured';
    muted = ' · ask an admin';
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="ai-status-pill"
          data-state-configured={configured ? 'true' : 'false'}
          className={cn(
            'inline-flex h-9 max-w-full items-center gap-2 rounded-full border border-line bg-surface px-3 text-[13px] text-ink shadow-card transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <ToneDot tone={tone} size={8} />
          <span className="truncate">
            <span className={cn('font-semibold', !loading && !configured && !error && 'text-status-red-ink')}>{main}</span>
            {muted ? <span className="hidden text-ink-3 sm:inline">{muted}</span> : null}
          </span>
          <Info className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
          <span className="sr-only">About the AI assistant</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] rounded-xl border-line bg-surface p-4 text-[12.5px] text-ink-2 shadow-overlay">
        {configured ? (
          <>
            <p className="font-semibold text-ink">Answers come from live Sara data</p>
            <p className="mt-1 text-ink-3">It reads the projects, invoices, tasks, procurement and stock records your role can see; the question and those records are sent to Gemini to write the answer.</p>
            <p className="mt-3 font-semibold text-ink">It can make only these changes</p>
            <ul className="mt-1 space-y-0.5">
              {actions.map((action) => (
                <li key={action.key} className="flex items-center gap-1.5"><ToneDot tone="green" size={6} />{action.label}</li>
              ))}
            </ul>
            <p className="mt-1 text-ink-3">Every change asks you to confirm it first, then shows a card with Undo.</p>
            {status.model ? <p className="mt-3 text-[11.5px] text-ink-3">Model: <span className="font-mono">{status.model}</span></p> : null}
          </>
        ) : error ? (
          <p>Couldn't check the AI status. Questions may still work.</p>
        ) : loading ? (
          <p>Checking the AI connection…</p>
        ) : (
          <>
            <p className="font-semibold text-ink">AI is not configured</p>
            <p className="mt-1 text-ink-3">The Gemini API key is missing on the server, so questions can't be answered yet. Ask an admin to add it.</p>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
